<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import addDays from 'date-fns/addDays';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import endOfDay from 'date-fns/endOfDay';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import { getQuantileIntervals } from '@chatwoot/utils';
import reportsAPI from 'dashboard/api/reports';
import {
  downloadCsvFile,
  generateFileName,
} from 'dashboard/helper/downloadHelper';
import BarChart from 'shared/components/charts/BarChart.vue';
import {
  buildDatasetHoverDimmingHandler,
  buildIntercomLikeBarTooltip,
  removeTooltipEl,
} from 'shared/components/charts/barInteractions';
import CompactDateInboxFilters from './components/CompactDateInboxFilters.vue';

const TOTAL_CHATS_METRIC_KEY = 'conversations_count';
const HEATMAP_QUANTILES = [0.2, 0.4, 0.6, 0.8, 0.9, 0.99];

const ENGAGEMENT_METRICS = [
  {
    key: 'conversations_from_campaigns_count',
    labelKey: 'CHAT_ENGAGEMENT_REPORT.FROM_CAMPAIGNS',
    colorHex: '#1D75E5',
    colorClass: 'bg-n-blue-9',
  },
  {
    key: 'conversations_started_by_customer_count',
    labelKey: 'CHAT_ENGAGEMENT_REPORT.STARTED_BY_CUSTOMER',
    colorHex: '#E7C51B',
    colorClass: 'bg-n-amber-9',
  },
  {
    key: 'conversations_started_by_agent_count',
    labelKey: 'CHAT_ENGAGEMENT_REPORT.STARTED_BY_AGENT',
    colorHex: '#C23881',
    colorClass: 'bg-n-ruby-9',
  },
];

const { t } = useI18n();

const filters = ref({
  from: 0,
  to: 0,
  inboxId: null,
});
const isHourlyDistributionEnabled = ref(false);
const isLoading = ref(false);
const isHeatmapLoading = ref(false);
const comparisonError = ref('');
const heatmapError = ref('');
const currentSummary = ref({});
const currentSeriesByMetric = ref({});
const heatmapSeries = ref([]);
const generatedAt = ref(null);
const isBreakdownOpen = ref(true);
const latestRefreshToken = ref(0);

const createRefreshToken = () => {
  latestRefreshToken.value += 1;
  return latestRefreshToken.value;
};

const isLatestRefresh = refreshToken =>
  refreshToken === latestRefreshToken.value;
const reportScope = computed(() => {
  if (filters.value.inboxId) {
    return { type: 'inbox', id: filters.value.inboxId };
  }
  return { type: 'account', id: undefined };
});

const currentPeriod = computed(() => {
  const fromTs = Number(filters.value.from || 0);
  const toTs = Number(filters.value.to || 0);
  if (!fromTs || !toTs) {
    return {
      from: 0,
      to: 0,
      fromDate: startOfDay(new Date()),
      toDate: endOfDay(new Date()),
      days: 1,
    };
  }

  const fromDate = startOfDay(fromUnixTime(fromTs));
  const toDate = endOfDay(fromUnixTime(toTs));
  const days = Math.max(differenceInCalendarDays(toDate, fromDate) + 1, 1);

  return {
    from: getUnixTime(fromDate),
    to: getUnixTime(toDate),
    fromDate,
    toDate,
    days,
  };
});

const formatPeriodLabel = period => {
  return `${format(fromUnixTime(period.from), 'dd MMM yy')} - ${format(
    fromUnixTime(period.to),
    'dd MMM yy'
  )}`;
};

const currentPeriodLabel = computed(() =>
  formatPeriodLabel(currentPeriod.value)
);

const normalizeSeries = series => {
  if (!Array.isArray(series)) return [];
  return [...series].sort((left, right) => left.timestamp - right.timestamp);
};

const sumSeries = series =>
  series.reduce((sum, item) => sum + Number(item.value || 0), 0);

const getMetricSeries = (source, metricKey) => source?.[metricKey] || [];

const getMetricValue = (summary, metricKey, fallbackSeries) => {
  const value = summary?.[metricKey];
  if (value === undefined || value === null) {
    return sumSeries(fallbackSeries);
  }

  return Number(value || 0);
};

const dayRows = computed(() => {
  return Array.from({ length: currentPeriod.value.days }, (_, index) => {
    const ts = getUnixTime(addDays(currentPeriod.value.fromDate, index));
    return {
      index,
      timestamp: ts,
      shortLabel: format(fromUnixTime(ts), 'dd MMM'),
      tooltipLabel: format(fromUnixTime(ts), "dd MMM''yy"),
      breakdownLabel: format(fromUnixTime(ts), 'dd MMM yy'),
    };
  });
});

const currentCategorizedTotal = computed(() =>
  ENGAGEMENT_METRICS.reduce((sum, metric) => {
    const series = getMetricSeries(currentSeriesByMetric.value, metric.key);
    const value = getMetricValue(currentSummary.value, metric.key, series);
    return sum + value;
  }, 0)
);

const hasEngagementData = computed(() => currentCategorizedTotal.value > 0);

const toPercentageLabel = (value, total) => {
  if (!total) return '0%';
  return `${((value / total) * 100).toFixed(2)}%`;
};

const summaryItems = computed(() =>
  ENGAGEMENT_METRICS.map(metric => {
    const currentSeries = getMetricSeries(
      currentSeriesByMetric.value,
      metric.key
    );
    const currentValue = getMetricValue(
      currentSummary.value,
      metric.key,
      currentSeries
    );
    return {
      key: metric.key,
      metricLabel: t(metric.labelKey),
      value: currentValue,
      percentage: toPercentageLabel(
        currentValue,
        currentCategorizedTotal.value
      ),
      colorClass: metric.colorClass,
    };
  })
);

const chartCollection = computed(() => ({
  labels: dayRows.value.map(day => day.shortLabel),
  datasets: ENGAGEMENT_METRICS.map(metric => {
    const series = getMetricSeries(currentSeriesByMetric.value, metric.key);
    const map = series.reduce((acc, item) => {
      const dayKey = format(fromUnixTime(item.timestamp), 'yyyy-MM-dd');
      acc[dayKey] = Number(item.value || 0);
      return acc;
    }, {});
    return {
      label: t(metric.labelKey),
      data: dayRows.value.map(day => {
        const dayKey = format(fromUnixTime(day.timestamp), 'yyyy-MM-dd');
        return Number(map[dayKey] || 0);
      }),
      backgroundColor: metric.colorHex,
      borderRadius: 4,
      categoryPercentage: 0.72,
      barPercentage: 0.95,
    };
  }),
}));

const tooltipId = 'chat-engagement-tooltip';

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 0 },
  interaction: { mode: 'nearest', intersect: true },
  onHover: buildDatasetHoverDimmingHandler(),
  plugins: {
    legend: { display: false },
    tooltip: {
      enabled: false,
      displayColors: false,
      external: buildIntercomLikeBarTooltip({
        tooltipId,
        titleForIndex: index => dayRows.value[index]?.tooltipLabel || '',
        seriesLabelForDataset: datasetIndex =>
          ENGAGEMENT_METRICS[datasetIndex]
            ? t(ENGAGEMENT_METRICS[datasetIndex].labelKey)
            : '',
        percentDigits: 0,
        showPercent: true,
      }),
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: { callback: value => Number(value).toLocaleString() },
    },
  },
}));

const breakdownRows = computed(() => {
  return ENGAGEMENT_METRICS.map(metric => {
    const series = getMetricSeries(currentSeriesByMetric.value, metric.key);
    const map = series.reduce((acc, item) => {
      const dayKey = format(fromUnixTime(item.timestamp), 'yyyy-MM-dd');
      acc[dayKey] = Number(item.value || 0);
      return acc;
    }, {});
    const values = dayRows.value.map(day => {
      const dayKey = format(fromUnixTime(day.timestamp), 'yyyy-MM-dd');
      return Number(map[dayKey] || 0);
    });
    return {
      key: metric.key,
      label: t(metric.labelKey),
      values,
      total: values.reduce((sum, v) => sum + Number(v || 0), 0),
    };
  });
});

const dayColumns = computed(() => {
  return Array.from({ length: currentPeriod.value.days }, (_, index) => {
    const date = addDays(currentPeriod.value.fromDate, index);
    return {
      key: format(date, 'yyyy-MM-dd'),
      label: format(date, 'dd MMM'),
      tooltipLabel: format(date, 'dd MMM yy'),
    };
  });
});

const hourlyRows = computed(() => {
  return Array.from({ length: 24 }, (_, hour) => hour);
});

const heatmapValueMap = computed(() => {
  return heatmapSeries.value.reduce((acc, item) => {
    const date = fromUnixTime(item.timestamp);
    const dayKey = format(date, 'yyyy-MM-dd');
    const key = `${dayKey}-${date.getHours()}`;
    acc[key] = Number(item.value || 0);
    return acc;
  }, {});
});

const heatmapRows = computed(() => {
  return hourlyRows.value.map(hour => {
    return {
      hour,
      cells: dayColumns.value.map(day => {
        const key = `${day.key}-${hour}`;
        const value = heatmapValueMap.value[key] || 0;
        return {
          key,
          value,
          dayLabel: day.tooltipLabel,
        };
      }),
    };
  });
});

const heatmapValues = computed(() =>
  Object.values(heatmapValueMap.value).map(value => Number(value || 0))
);

const heatmapQuantiles = computed(() =>
  getQuantileIntervals(heatmapValues.value, HEATMAP_QUANTILES)
);

const getHeatmapCellClass = value => {
  if (!value) return 'bg-n-slate-2';

  let level = [...heatmapQuantiles.value, Infinity].findIndex(
    range => value <= range && value > 0
  );

  if (level > 6) level = 5;
  if (level === 0) return 'bg-n-slate-2';

  const classes = [
    'bg-n-blue-3',
    'bg-n-blue-5',
    'bg-n-blue-7',
    'bg-n-blue-8',
    'bg-n-blue-10',
    'bg-n-blue-11',
  ];

  return classes[level - 1];
};

const formatHourLabel = hour => {
  const hour12 = hour % 12 || 12;
  const suffix = hour < 12 ? 'am' : 'pm';
  return `${hour12}:00 ${suffix}`;
};

const generatedAtLabel = computed(() => {
  if (!generatedAt.value) {
    return t('LAST_7_DAYS_REPORT.NOT_GENERATED_YET');
  }

  return generatedAt.value.toLocaleString();
});

const toCsvValue = value => {
  const stringValue = String(value ?? '');
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const rowsToCsv = (headers, rows) => {
  const csvRows = [headers, ...rows];
  return csvRows
    .map(row => row.map(column => toCsvValue(column)).join(','))
    .join('\n');
};

const downloadCurrentViewCsv = () => {
  if (isHourlyDistributionEnabled.value) {
    const headers = [
      t('TOTAL_CHATS_REPORT.HOUR'),
      ...dayColumns.value.map(day => day.label),
    ];
    const rows = heatmapRows.value.map(row => [
      formatHourLabel(row.hour),
      ...row.cells.map(cell => cell.value),
    ]);
    const csvContent = rowsToCsv(headers, rows);
    const fileName = generateFileName({
      type: 'chat-engagement-heatmap',
      to: currentPeriod.value.to,
    });
    downloadCsvFile(fileName, csvContent);
    return;
  }

  const headers = [
    t('CHAT_ENGAGEMENT_REPORT.SERIES'),
    ...dayRows.value.map(day => day.breakdownLabel),
    'Total',
  ];

  const rows = breakdownRows.value.map(row => [
    row.label,
    ...row.values,
    row.total,
  ]);

  const csvContent = rowsToCsv(headers, rows);
  const fileName = generateFileName({
    type: 'chat-engagement-breakdown',
    to: currentPeriod.value.to,
  });
  downloadCsvFile(fileName, csvContent);
};

const clearSeriesData = () => {
  currentSeriesByMetric.value = {};
};

const fetchComparisonData = async refreshToken => {
  if (!currentPeriod.value.from || !currentPeriod.value.to) return;
  if (isLatestRefresh(refreshToken)) {
    isLoading.value = true;
    comparisonError.value = '';
  }

  try {
    const [currentSeriesResponses, currentSummaryResponse] = await Promise.all([
      Promise.all(
        ENGAGEMENT_METRICS.map(metric =>
          reportsAPI.getReports({
            metric: metric.key,
            from: currentPeriod.value.from,
            to: currentPeriod.value.to,
            type: reportScope.value.type,
            id: reportScope.value.id,
            groupBy: 'day',
            businessHours: false,
          })
        )
      ),
      reportsAPI.getSummary(
        currentPeriod.value.from,
        currentPeriod.value.to,
        reportScope.value.type,
        reportScope.value.id,
        'day',
        false,
        false
      ),
    ]);

    if (!isLatestRefresh(refreshToken)) return;

    currentSeriesByMetric.value = ENGAGEMENT_METRICS.reduce(
      (acc, metric, idx) => {
        acc[metric.key] = normalizeSeries(currentSeriesResponses[idx].data);
        return acc;
      },
      {}
    );
    currentSummary.value = currentSummaryResponse.data || {};
    generatedAt.value = new Date();
  } catch {
    if (!isLatestRefresh(refreshToken)) return;
    comparisonError.value = t('CHAT_ENGAGEMENT_REPORT.FETCH_ERROR');
    clearSeriesData();
    currentSummary.value = {};
  } finally {
    if (isLatestRefresh(refreshToken)) {
      isLoading.value = false;
    }
  }
};

const fetchHeatmapData = async refreshToken => {
  if (!currentPeriod.value.from || !currentPeriod.value.to) return;
  if (isLatestRefresh(refreshToken)) {
    isHeatmapLoading.value = true;
    heatmapError.value = '';
  }

  try {
    const response = await reportsAPI.getReports({
      metric: TOTAL_CHATS_METRIC_KEY,
      from: currentPeriod.value.from,
      to: currentPeriod.value.to,
      type: reportScope.value.type,
      id: reportScope.value.id,
      groupBy: 'hour',
      businessHours: false,
    });

    if (!isLatestRefresh(refreshToken)) return;

    heatmapSeries.value = normalizeSeries(response.data);
    generatedAt.value = new Date();
  } catch {
    if (!isLatestRefresh(refreshToken)) return;
    heatmapError.value = t('CHAT_ENGAGEMENT_REPORT.FETCH_ERROR');
    heatmapSeries.value = [];
  } finally {
    if (isLatestRefresh(refreshToken)) {
      isHeatmapLoading.value = false;
    }
  }
};

const refreshData = async () => {
  const refreshToken = createRefreshToken();
  const tasks = [fetchComparisonData(refreshToken)];
  if (isHourlyDistributionEnabled.value) {
    tasks.push(fetchHeatmapData(refreshToken));
  }
  await Promise.all(tasks);
};

watch(isHourlyDistributionEnabled, async enabled => {
  if (!enabled) return;
  const refreshToken = createRefreshToken();
  await fetchHeatmapData(refreshToken);
});

const onFilterChange = ({ from, to, selectedInbox }) => {
  filters.value = {
    from,
    to,
    inboxId: selectedInbox?.id || null,
  };
  refreshData();
};

onMounted(async () => {
  // Initial fetch happens when filters emit their initial value.
});

onBeforeUnmount(() => {
  removeTooltipEl(tooltipId);
});
</script>

<template>
  <div class="ce-page">
    <div class="ce-shell">
      <div class="ce-header">
        <div class="ce-header-left">
          <h1 class="ce-title">{{ t('CHAT_ENGAGEMENT_REPORT.TITLE') }}</h1>
        </div>
      </div>

      <div class="ce-toolbar">
        <CompactDateInboxFilters
          :show-add-filter="false"
          @filter-change="onFilterChange"
        />
        <div class="ce-toolbar-right">
          <span class="ce-toggle-label">
            {{ t('TOTAL_CHATS_REPORT.TWENTY_FOUR_HOUR_DISTRIBUTION') }}
          </span>
          <button
            class="crt-switch"
            :class="{ 'is-on': isHourlyDistributionEnabled }"
            type="button"
            @click="isHourlyDistributionEnabled = !isHourlyDistributionEnabled"
          >
            <span />
          </button>
        </div>
      </div>

      <div class="ce-content">
        <div v-if="comparisonError" class="ce-error">
          {{ comparisonError }}
        </div>

        <section class="ce-card">
          <div class="ce-card-header">
            <div class="ce-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                />
              </svg>
              <h2 class="ce-card-title">
                {{ t('CHAT_ENGAGEMENT_REPORT.TITLE') }}
              </h2>
              <span class="ce-period">{{ currentPeriodLabel }}</span>
            </div>
            <button
              class="ce-export-btn"
              type="button"
              @click="downloadCurrentViewCsv"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                />
              </svg>
              <span>{{ t('CHAT_ENGAGEMENT_REPORT.EXPORT_CSV') }}</span>
            </button>
          </div>

          <div
            class="ce-section"
            :class="{ 'ce-section--no-bottom': !isHourlyDistributionEnabled }"
          >
            <div v-if="!isHourlyDistributionEnabled" class="ce-stack">
              <div class="ce-summary-row">
                <div
                  v-for="item in summaryItems"
                  :key="item.key"
                  class="ce-summary-item"
                >
                  <div class="ce-summary-item-label">
                    <span class="ce-color-box" :class="item.colorClass" />
                    <span>{{ item.metricLabel }}</span>
                  </div>
                  <div class="ce-summary-item-value">
                    <span class="ce-summary-number">{{
                      item.value.toLocaleString()
                    }}</span>
                    <span class="ce-summary-pct">{{ item.percentage }}</span>
                  </div>
                </div>
              </div>

              <div class="ce-chart">
                <woot-loading-state
                  v-if="isLoading"
                  :message="$t('REPORT.LOADING_CHART')"
                  class="h-full"
                />
                <div v-else-if="!hasEngagementData" class="ce-empty">
                  {{ t('CHAT_ENGAGEMENT_REPORT.NO_DATA') }}
                </div>
                <BarChart
                  v-else
                  :collection="chartCollection"
                  :chart-options="chartOptions"
                />
              </div>
            </div>

            <div v-else class="ce-stack">
              <div class="ce-heatmap-title">
                <div class="ce-heatmap-h">
                  {{ t('CHAT_ENGAGEMENT_REPORT.HEATMAP_TITLE') }}
                </div>
                <div class="ce-heatmap-sub">
                  {{ t('CHAT_ENGAGEMENT_REPORT.HEATMAP_LIMITATION') }}
                </div>
              </div>

              <div class="ce-heatmap-wrap">
                <woot-loading-state
                  v-if="isHeatmapLoading"
                  :message="$t('REPORT.LOADING_CHART')"
                />

                <div v-else-if="heatmapError" class="ce-empty">
                  {{ heatmapError }}
                </div>

                <table v-else-if="heatmapRows.length" class="ce-heatmap-table">
                  <thead>
                    <tr>
                      <th class="ce-heatmap-th ce-heatmap-th--hour">
                        {{ t('TOTAL_CHATS_REPORT.HOUR') }}
                      </th>
                      <th
                        v-for="day in dayColumns"
                        :key="day.key"
                        class="ce-heatmap-th"
                      >
                        {{ day.label }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in heatmapRows" :key="`hour-${row.hour}`">
                      <td class="ce-heatmap-td ce-heatmap-td--hour">
                        {{
                          row.hour % 3 === 0 ? formatHourLabel(row.hour) : ''
                        }}
                      </td>
                      <td
                        v-for="cell in row.cells"
                        :key="cell.key"
                        class="ce-heatmap-td"
                        :title="`${cell.dayLabel} ${formatHourLabel(row.hour)}: ${cell.value}`"
                      >
                        <div
                          class="ce-heatmap-cell"
                          :class="getHeatmapCellClass(cell.value)"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div v-else class="ce-empty">
                  {{ t('CHAT_ENGAGEMENT_REPORT.NO_DATA') }}
                </div>
              </div>
            </div>
          </div>

          <div v-if="!isHourlyDistributionEnabled" class="ce-breakdown">
            <div
              class="ce-breakdown-header"
              role="button"
              tabindex="0"
              @click="isBreakdownOpen = !isBreakdownOpen"
              @keydown.enter.prevent="isBreakdownOpen = !isBreakdownOpen"
              @keydown.space.prevent="isBreakdownOpen = !isBreakdownOpen"
            >
              <div class="ce-breakdown-title-row">
                <svg
                  class="ce-breakdown-caret"
                  :class="{ 'is-collapsed': !isBreakdownOpen }"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m6 9 6 6 6-6"
                  />
                </svg>
                <span>{{ t('CHAT_ENGAGEMENT_REPORT.BREAKDOWN_TITLE') }}</span>
              </div>

              <button
                class="ce-export-btn"
                type="button"
                @click.stop="downloadCurrentViewCsv"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                  />
                </svg>
                <span>{{ t('CHAT_ENGAGEMENT_REPORT.EXPORT_CSV') }}</span>
              </button>
            </div>

            <div v-show="isBreakdownOpen" class="ce-table-wrap">
              <table class="ce-table">
                <thead>
                  <tr>
                    <th class="ce-th-series">
                      {{ t('CHAT_ENGAGEMENT_REPORT.SERIES') }}
                    </th>
                    <th
                      v-for="day in dayRows"
                      :key="`header-${day.timestamp}`"
                      class="ce-th-data"
                    >
                      <div>{{ day.breakdownLabel }}</div>
                    </th>
                    <th class="ce-th-data">
                      <div>Total</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in breakdownRows" :key="row.key">
                    <td class="ce-td-series">{{ row.label }}</td>
                    <td
                      v-for="(value, idx) in row.values"
                      :key="`${row.key}-${idx}`"
                      class="ce-td-data"
                    >
                      <div class="ce-cell-main">
                        {{ Number(value || 0).toLocaleString() }}
                      </div>
                    </td>
                    <td class="ce-td-data">
                      <div class="ce-cell-main">
                        {{ row.total.toLocaleString() }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <p class="ce-footer-note">
          {{
            t('CHAT_ENGAGEMENT_REPORT.GENERATED_AT', {
              time: generatedAtLabel,
            })
          }}<br />
          {{ t('LAST_7_DAYS_REPORT.LOCAL_TIME_NOTE') }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ce-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  background: #ffffff;
  color: #111827;
}

.ce-shell {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: visible;
  min-width: 650px;
}

.ce-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
  position: sticky;
  top: 0;
  z-index: 50;
  background: #ffffff;
}

.ce-header-left {
  display: flex;
  align-items: center;
  gap: 0;
}

.ce-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}

.ce-toolbar {
  padding: 12px 24px 8px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0 3px 10px -6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  position: sticky;
  top: 58px;
  z-index: 49;
}

.ce-toolbar-right {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.ce-toggle-label {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.crt-switch {
  width: 27px;
  height: 20px;
  border: none;
  border-radius: 15px;
  box-sizing: border-box;
  cursor: pointer;
  background: rgb(221, 221, 221);
  padding: 0;
  display: block;
  position: relative;
}

.crt-switch span {
  position: absolute;
  display: block;
  top: 2px;
  left: 2px;
  border-radius: 50%;
  background-color: rgb(255, 255, 255);
  box-shadow:
    rgba(0, 0, 0, 0.05) 0 3px 3px 0,
    rgba(0, 0, 0, 0.1) 0 2px 2px 0,
    rgba(0, 0, 0, 0.05) 0 3px 2px 0;
  transition:
    left 0.2s ease-in-out,
    background-color 0.2s ease-in-out;
  will-change: left, background-color;
  width: 15px;
  height: 15px;
}

.crt-switch.is-on {
  background: #2f80ed;
}

.crt-switch.is-on span {
  left: 10px;
}

.ce-content {
  padding: 16px 32px;
}

.ce-error {
  margin-bottom: 12px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}

.ce-card {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #ffffff;
  box-sizing: border-box;
  margin-bottom: 20px;
  overflow: hidden;
}

.ce-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.ce-card-title-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.ce-card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.ce-period {
  font-size: 12px;
  color: #6b7280;
  margin-left: 10px;
  white-space: nowrap;
}

.ce-export-btn {
  min-width: 128px;
  height: 36px;
  padding: 0 16px 0 12px;
  border-radius: 8px;
  border: 1px solid #d9dde3;
  background: #ffffff;
  color: #111827;
  font-weight: 600;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
}

.ce-export-btn:hover {
  background: #f9fafb;
}

.ce-section {
  padding: 16px 20px 20px;
}

.ce-section--no-bottom {
  padding-bottom: 0;
}

.ce-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ce-summary-grid {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 12px;
}

@media (min-width: 860px) {
  .ce-summary-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.ce-summary-row {
  display: flex;
  flex-wrap: wrap;
  gap: 18px 22px;
  align-items: flex-start;
}

.ce-summary-item {
  min-width: 160px;
}

.ce-summary-item-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.ce-summary-item-value {
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.ce-summary-number {
  font-size: 18px;
  line-height: 22px;
  font-weight: 700;
  color: #111827;
}

.ce-summary-pct {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.ce-summary-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f8fafc;
  padding: 12px;
}

.ce-summary-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.ce-color-box {
  width: 12px;
  height: 12px;
  border-radius: 3px;
}

.ce-summary-sub {
  margin: 4px 0 0;
  font-size: 12px;
  color: #6b7280;
}

.ce-summary-value-row {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.ce-summary-value {
  font-size: 28px;
  line-height: 32px;
  font-weight: 700;
  color: #111827;
}

.ce-summary-badge {
  border-radius: 6px;
  background: #e5e7eb;
  color: #374151;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 6px;
}

.ce-chart {
  height: 384px;
}

.ce-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 13px;
}

.ce-breakdown {
  border-top: 1px solid #dfe3e8;
}

.ce-breakdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 15px 16px;
  cursor: pointer;
  user-select: none;
}

.ce-breakdown-title-row {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.ce-breakdown-caret {
  transition: transform 0.15s ease;
}

.ce-breakdown-caret.is-collapsed {
  transform: rotate(-90deg);
}

.ce-table-wrap {
  overflow-x: auto;
  border-top: 1px solid #dfe3e8;
}

.ce-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 14px;
  color: #424d57;
}

.ce-th-series,
.ce-td-series {
  position: sticky;
  left: 0;
  min-width: 220px;
  max-width: 220px;
  text-align: left;
  border-right: 1px solid #d7dde4;
  z-index: 2;
  background: #ffffff;
}

.ce-th-series {
  background: #f8fafc;
  padding: 10px 12px;
  font-weight: 700;
  z-index: 3;
}

.ce-th-data {
  background: #f8fafc;
  padding: 10px 12px;
  text-align: center;
  border-bottom: 1px solid #dfe3e8;
  white-space: nowrap;
  font-weight: 700;
}

.ce-th-sub {
  margin-top: 2px;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
}

.ce-td-series {
  padding: 12px;
  font-weight: 700;
  color: #111827;
}

.ce-td-data {
  padding: 12px;
  text-align: center;
  border-bottom: 1px solid #edf1f5;
  white-space: nowrap;
  vertical-align: top;
}

.ce-cell-main {
  font-weight: 700;
  color: #111827;
}

.ce-cell-sub {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}

.ce-cell-diff {
  font-size: 12px;
  font-weight: 700;
  margin-top: 2px;
}

.ce-heatmap-title {
  margin-bottom: 4px;
}

.ce-heatmap-h {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.ce-heatmap-sub {
  margin-top: 4px;
  font-size: 13px;
  color: #b91c1c;
}

.ce-heatmap-wrap {
  overflow-x: auto;
}

.ce-heatmap-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 4px;
}

.ce-heatmap-th {
  padding: 4px 6px;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: #111827;
}

.ce-heatmap-th--hour {
  text-align: left;
  color: #6b7280;
}

.ce-heatmap-td {
  padding: 2px;
}

.ce-heatmap-td--hour {
  font-size: 12px;
  color: #6b7280;
  padding-right: 10px;
  white-space: nowrap;
}

.ce-heatmap-cell {
  height: 24px;
  width: 56px;
  border-radius: 4px;
}

.ce-footer-note {
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}

::global(body.dark) .ce-page,
::global(body.dark) .ce-shell,
::global(body.dark) .ce-card,
::global(body.dark) .ce-header,
::global(body.dark) .ce-toolbar,
::global(body.dark) .ce-th-series,
::global(body.dark) .ce-th-data,
::global(body.dark) .ce-td-series,
::global(body.dark) .ce-td-data,
::global(body.theme-mix) .ce-page,
::global(body.theme-mix) .ce-shell,
::global(body.theme-mix) .ce-card,
::global(body.theme-mix) .ce-header,
::global(body.theme-mix) .ce-toolbar,
::global(body.theme-mix) .ce-th-series,
::global(body.theme-mix) .ce-th-data,
::global(body.theme-mix) .ce-td-series,
::global(body.theme-mix) .ce-td-data {
  background: #ffffff !important;
  color: #111827 !important;
}

::global(body.dark) .ce-th-series *,
::global(body.dark) .ce-th-data *,
::global(body.theme-mix) .ce-th-series *,
::global(body.theme-mix) .ce-th-data * {
  color: #111827 !important;
}
</style>
